import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { css as cssLang } from '@codemirror/lang-css';
import { html as htmlLang } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import CodeMirror from '@uiw/react-codemirror';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from './Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './Dialog';
export default function CodeInputModal({ open, onClose, title = 'Code Editor', language = 'html', value = '', onChange, onSave, disabled, placeholder, }) {
    const [wrap] = useState(true);
    const localValRef = useRef(value);
    const [containerHeight, setContainerHeight] = useState(500);
    // Sync ref when value prop changes
    useEffect(() => {
        localValRef.current = value;
    }, [value]);
    // Calculate container height based on viewport
    const calculateHeight = useCallback(() => {
        const viewportHeight = window.innerHeight;
        const dialogPadding = 150;
        const calculatedHeight = Math.min(Math.max(viewportHeight - dialogPadding, 300), 800);
        setContainerHeight(calculatedHeight);
    }, []);
    useEffect(() => {
        if (open) {
            calculateHeight();
            window.addEventListener('resize', calculateHeight);
            return () => window.removeEventListener('resize', calculateHeight);
        }
    }, [open, calculateHeight]);
    // Choose CodeMirror language extension
    const cmExtensions = useMemo(() => {
        switch (language) {
            case 'css':
                return [cssLang()];
            case 'html':
                return [htmlLang()];
            case 'typescript':
                return [javascript({ typescript: true })];
            case 'tsx':
                return [javascript({ typescript: true, jsx: true })];
            case 'javascript':
            default:
                return [javascript()];
        }
    }, [language]);
    const handleSave = useCallback(() => {
        onSave === null || onSave === void 0 ? void 0 : onSave(localValRef.current);
        onChange === null || onChange === void 0 ? void 0 : onChange(localValRef.current);
        onClose();
    }, [onSave, onChange, onClose]);
    const handleCancel = useCallback(() => {
        // Reset to original value
        localValRef.current = value;
        onClose();
    }, [value, onClose]);
    return (_jsx(Dialog, { open: open, onOpenChange: (isOpen) => !isOpen && handleCancel(), children: _jsxs(DialogContent, { className: "max-w-7xl", showCloseButton: false, children: [_jsx(DialogHeader, { children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsx(DialogTitle, { children: title }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Button, { variant: 'outline', size: 'sm', onClick: handleCancel, children: "Cancel" }), _jsx(Button, { size: 'sm', onClick: handleSave, children: "Save" })] })] }) }), _jsx("div", { className: "relative mt-0 overflow-hidden", children: _jsx(CodeMirror, { value: value, onChange: (v) => {
                            localValRef.current = v;
                        }, extensions: cmExtensions, basicSetup: {
                            lineNumbers: true,
                            highlightActiveLine: true,
                            autocompletion: true,
                            foldGutter: true,
                            bracketMatching: true,
                        }, height: `${containerHeight}px`, minWidth: "100%", editable: !disabled, placeholder: placeholder, style: {
                            fontSize: 14,
                            borderRadius: 10,
                            whiteSpace: wrap
                                ? 'pre-wrap'
                                : 'pre',
                            wordBreak: wrap ? 'break-word' : 'normal',
                        } }) })] }) }));
}
